import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import Button from '@mui/material/Button';
import { getDemoContentAccounts, DemoContentAccountDiagnostics } from '../../services/demo-content.service';
import { toastService } from '../../services/toast.service';
import { format } from 'date-fns';

const formatDate = (iso: string | null) => (iso ? format(new Date(iso), 'dd.MM.yyyy HH:mm') : '—');

const STATUS_CHIP: Record<number, { label: string; color: 'success' | 'warning' | 'error' }> = {
  1: { label: 'Активний', color: 'success' },
  2: { label: 'Заблокований', color: 'warning' },
  3: { label: 'Видалений', color: 'error' },
};

const AccountRow: React.FC<{ account: DemoContentAccountDiagnostics }> = ({ account }) => {
  const [open, setOpen] = useState(false);
  const statusInfo = account.status != null ? STATUS_CHIP[account.status] : null;

  return (
    <>
      <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
        <TableCell>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{account.name || `Акаунт #${account.userId}`}</TableCell>
        <TableCell>{account.email}</TableCell>
        <TableCell>
          {statusInfo ? (
            <Chip label={statusInfo.label} size="small" color={statusInfo.color} variant="outlined" />
          ) : '—'}
        </TableCell>
        <TableCell>{formatDate(account.lastEventsAt)}</TableCell>
        <TableCell>{formatDate(account.lastMeasurementsAt)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" gutterBottom>Останні запуски наповнення</Typography>
              {account.recentLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Ще не наповнювався</Typography>
              ) : (
                account.recentLogs.map(log => (
                  <Box key={log.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                    <Chip
                      label={log.contentType === 'events' ? 'Події' : 'Вимірювання'}
                      size="small"
                      color={log.contentType === 'events' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                    <Typography variant="body2">{log.summary}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {formatDate(log.createdAt)}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const DevDemoContentDiagnostics: React.FC = () => {
  const [accounts, setAccounts] = useState<DemoContentAccountDiagnostics[]>([]);
  const [passwordHint, setPasswordHint] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDemoContentAccounts();
      setAccounts(data.accounts);
      setPasswordHint(data.passwordHint);
    } catch (error) {
      console.error('Failed to load demo content diagnostics', error);
      toastService.error('Не вдалося завантажити діагностику demo-акаунтів');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Demo-акаунти — діагностика авто-наповнення</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Оновити
        </Button>
      </Box>

      {passwordHint && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Пароль для всіх demo-акаунтів: <strong>{passwordHint}</strong>
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Крон додає demo-акаунтам кілька подій щодня і вимірювання раз на кілька днів (налаштування — в
        системних налаштуваннях, ключ <code>demo_content_generation</code>), а застарілі — прибирає. Ця
        сторінка — щоб бачити, що механізм справді працює.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : accounts.length === 0 ? (
        <Typography sx={{ mt: 4 }}>Немає жодного demo-content акаунта.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Ім'я</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Останні події</TableCell>
                <TableCell>Останні вимірювання</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map(account => (
                <AccountRow key={account.userId} account={account} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
